import { Platform } from 'react-native';
import * as SentryNative from '@sentry/react-native';
import * as SentryWeb from '@sentry/react';
import * as Application from 'expo-application';

const SENTRY_DSN = process.env.EXPO_PUBLIC_SENTRY_DSN;
const isWeb = Platform.OS === 'web';

// Create navigation integration for breadcrumbs (mobile only)
// Exported so AppNavigator can register the navigation container
export const navigationIntegration = isWeb
  ? null
  : SentryNative.reactNavigationIntegration({
      enableTimeToInitialDisplay: false,
    });

/**
 * Initialize Sentry error monitoring
 * Only runs in production when DSN is configured
 * Uses @sentry/react for web, @sentry/react-native for mobile
 */
export function initSentry(): void {
  // Skip in development or if DSN not configured
  if (__DEV__ || !SENTRY_DSN) {
    return;
  }

  if (isWeb) {
    // Web-specific initialization
    SentryWeb.init({
      dsn: SENTRY_DSN,
      environment: 'production',
      release: `clubswap@${Application.nativeApplicationVersion || '1.0.0'}`,
      sendDefaultPii: false,
      maxBreadcrumbs: 50,
      sampleRate: 1.0,
    });
  } else {
    // Mobile-specific initialization
    SentryNative.init({
      dsn: SENTRY_DSN,
      environment: 'production',
      release: `clubswap@${Application.nativeApplicationVersion || '1.0.0'}`,
      sendDefaultPii: false,
      enableAutoSessionTracking: true,
      maxBreadcrumbs: 50,
      sampleRate: 1.0,
      integrations: navigationIntegration ? [navigationIntegration] : [],
    });
  }
}

/**
 * Set the current user for error attribution
 * Call after successful login or session restore
 */
export function setSentryUser(userId: string): void {
  if (__DEV__) return;
  if (isWeb) {
    SentryWeb.setUser({ id: userId });
  } else {
    SentryNative.setUser({ id: userId });
  }
}

/**
 * Clear user context on logout
 */
export function clearSentryUser(): void {
  if (__DEV__) return;
  if (isWeb) {
    SentryWeb.setUser(null);
  } else {
    SentryNative.setUser(null);
  }
}

/**
 * Capture a handled error with context
 * Use for errors that show UI feedback but should still be tracked
 */
export function captureError(error: unknown, context?: string): void {
  if (__DEV__) return;
  const options = {
    tags: { context: context || 'unknown' },
    level: 'warning' as const,
  };
  if (isWeb) {
    SentryWeb.captureException(error, options);
  } else {
    SentryNative.captureException(error, options);
  }
}

/**
 * Capture a message (non-exception) event
 */
export function captureMessage(message: string, context?: string): void {
  if (__DEV__) return;
  const options = {
    tags: { context: context || 'unknown' },
    level: 'warning' as const,
  };
  if (isWeb) {
    SentryWeb.captureMessage(message, options);
  } else {
    SentryNative.captureMessage(message, options);
  }
}

// Re-export for navigation instrumentation (uses native SDK for mobile)
export { SentryNative as Sentry };
